import {
  STAFF_CATALOG_SEARCH_BOUNDS as BOUNDS,
  type StaffCatalogSearchDebug,
  type StaffCatalogSearchEmbeddingState,
  type StaffCatalogSearchFilterName,
  type StaffCatalogSearchHitReason,
  type StaffCatalogSearchOutcome,
  type StaffCatalogSearchReasonCode,
  type StaffCatalogSearchScores,
} from "@lafayette/schemas";

// The words of the search page. The API answers in codes. This module turns each code into a plain
// sentence, so a staff member never reads `near.outside_within`. A `Record` over the code type makes the
// compiler refuse a new code that has no sentence. Pure: no React.

const filterLabels: Readonly<Record<StaffCatalogSearchFilterName, string>> = {
  text: "Text",
  types: "Item types",
  location: "City or country",
  near: "Near a point",
  within: "Map box",
  price: "Price",
  pricingModels: "Pricing model",
  dietary: "Dietary tags",
  categories: "Categories",
  eventWindow: "Event dates",
  partySize: "Party size",
  fulfilmentModes: "How it is arranged",
  preferenceTags: "Preferences",
};

export const filterLabel = (name: StaffCatalogSearchFilterName): string => filterLabels[name];

/** `City or country, Near a point and Dietary tags`. */
export function listFilters(names: readonly StaffCatalogSearchFilterName[]): string {
  const labels = names.map(filterLabel);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

const reasonSentences: Readonly<Record<StaffCatalogSearchReasonCode, string>> = {
  "price.min_gt_max": "The lowest price is higher than the highest price.",
  "event_window.from_not_before_to": "The event window ends before it starts.",
  "party_size.not_applicable":
    "Party size applies to villas and experiences only. None of the chosen item types uses it.",
  "event_window.not_applicable":
    "Event dates apply to events only. None of the chosen item types uses them.",
  "near.outside_within": "The point you chose is outside the map box.",
  "text.too_many_tokens": `The text has too many words. Use at most ${BOUNDS.textTokens} words.`,
  "within.south_not_before_north": "The south edge of the map box must be below the north edge.",
  "within.west_not_before_east": "The west edge of the map box must be left of the east edge.",
  "within.too_large": "The map box is too large. Use at most 20 degrees on each side.",
  "event_window.too_long": "The event window is longer than 366 days.",
  "staff_field.not_allowed": "A staff-only option is not allowed in this request.",
  NO_TEXT_MATCH: "No item matched the text. The items below match the filters only.",
};

export const reasonSentence = (code: StaffCatalogSearchReasonCode): string => reasonSentences[code];

export const outcomeSummaries: Readonly<Record<StaffCatalogSearchOutcome, string>> = {
  OK: "The search ran.",
  NO_MATCH: "No item passes all the filters.",
  CONFLICTING_CONSTRAINTS: "The filters cannot all be true at the same time. No search ran.",
  INVALID: "The search is not valid. No search ran.",
};

export const SEMANTIC_UNAVAILABLE_NOTICE =
  "Meaning search is off or did not answer. Results use names, words and distance only.";

export const hitReasonLabels: Readonly<Record<StaffCatalogSearchHitReason, string>> = {
  NAME_EXACT: "Exact name",
  NAME_PHRASE: "Name holds the phrase",
  NAME_PREFIX: "Name starts with the words",
  TEXT_MATCH: "Words match",
  SEMANTIC_MATCH: "Meaning matches",
  WITHIN_RADIUS: "Inside the radius",
  EDITORIAL_BOOST: "Editorial boost",
  FRESHNESS_BOOST: "Recently verified",
  PREFERENCE_BOOST: "Preference match",
};

export const collapseReasonLabels = {
  SAME_NAME: "same name",
  SIMILAR_VECTOR: "similar meaning",
} as const;

/** Every embedding state is a glyph and a word, so it never depends on colour. */
export const embeddingStateLabels: Readonly<
  Record<StaffCatalogSearchEmbeddingState, Readonly<{ glyph: string; label: string; help: string }>>
> = {
  READY: { glyph: "✓", label: "Ready", help: "The meaning vector is current." },
  STALE: {
    glyph: "↻",
    label: "Stale",
    help: "The item changed after its meaning vector was made.",
  },
  PENDING: { glyph: "…", label: "Pending", help: "The meaning vector is waiting to be made." },
  FAILED: { glyph: "✕", label: "Failed", help: "The meaning vector could not be made." },
  NONE: { glyph: "–", label: "None", help: "The item has no meaning vector." },
};

export const vectorPathLabels: Readonly<Record<StaffCatalogSearchDebug["vectorPath"], string>> = {
  EXACT: "Exact scan",
  HNSW: "Index (HNSW)",
  NONE: "Not used",
};

export type ScoreRow = Readonly<{
  key: keyof StaffCatalogSearchScores;
  label: string;
  value: string;
}>;

const NOT_RANKED = "Not ranked";
const decimals = (value: number, places: number) => value.toFixed(places);
const rank = (value: number | null) => (value === null ? NOT_RANKED : `#${value}`);

/** The scores of one hit as label and text, in the order the ranking applies them. */
export function scoreRows(scores: StaffCatalogSearchScores): readonly ScoreRow[] {
  return [
    { key: "final", label: "Final score", value: decimals(scores.final, 5) },
    { key: "fused", label: "Fused score", value: decimals(scores.fused, 5) },
    {
      key: "nameTier",
      label: "Name tier",
      value: scores.nameTier === null ? "No name match" : `Tier ${scores.nameTier}`,
    },
    { key: "lexicalRank", label: "Word rank", value: rank(scores.lexicalRank) },
    { key: "vectorRank", label: "Meaning rank", value: rank(scores.vectorRank) },
    { key: "geoRank", label: "Distance rank", value: rank(scores.geoRank) },
    { key: "editorialBoost", label: "Editorial boost", value: decimals(scores.editorialBoost, 3) },
    { key: "freshnessBoost", label: "Freshness boost", value: decimals(scores.freshnessBoost, 3) },
    {
      key: "preferenceBoost",
      label: "Preference boost",
      value: decimals(scores.preferenceBoost, 3),
    },
  ];
}
